/**
 * HandlerRegistryService
 *
 * Concrete implementation of IHandlerRegistry
 * Manages event → handler mapping with validation
 *
 * Single Responsibility: Handler discovery and registration
 */

import type { IHandlerRegistry, MessageHandler, HandlerMetadata } from '../interfaces';
import { isValidSignalREvent, getRequiredEvents } from '../types';

export class HandlerRegistryService implements IHandlerRegistry {
  private handlers: Map<string, MessageHandler[]> = new Map();
  private metadata: Map<string, HandlerMetadata[]> = new Map();

  register(event: string, handler: MessageHandler, meta?: Partial<HandlerMetadata>): void {
    // Validate event name
    if (!isValidSignalREvent(event)) {
      throw new Error(`Invalid SignalR event: ${event}. Must be one of the known events.`);
    }

    if (!handler || typeof handler !== 'function') {
      throw new Error('Handler must be a function');
    }

    // Get or create handler list
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
      this.metadata.set(event, []);
    }

    // Add handler
    this.handlers.get(event)!.push(handler);

    // Store metadata
    const handlerMeta: HandlerMetadata = {
      name: handler.name || `AnonymousHandler#${this.handlers.get(event)!.length}`,
      eventName: event,
      ...meta,
    };
    this.metadata.get(event)!.push(handlerMeta);
  }

  getHandlers(event: string): MessageHandler[] {
    return this.handlers.get(event) || [];
  }

  hasHandler(event: string): boolean {
    const handlers = this.handlers.get(event);
    return handlers !== undefined && handlers.length > 0;
  }

  handlerCount(event: string): number {
    return this.handlers.get(event)?.length ?? 0;
  }

  getMetadata(event: string): HandlerMetadata[] {
    return this.metadata.get(event) || [];
  }

  getAllRegistered(): string[] {
    return Array.from(this.handlers.keys()).filter((event) => this.hasHandler(event));
  }

  unregister(event: string, handler: MessageHandler): boolean {
    const handlers = this.handlers.get(event);
    if (!handlers) return false;

    const index = handlers.indexOf(handler);
    if (index === -1) return false;

    handlers.splice(index, 1);

    // Also remove metadata
    const metas = this.metadata.get(event);
    if (metas && index < metas.length) {
      metas.splice(index, 1);
    }

    return true;
  }

  clearHandlers(event: string): void {
    this.handlers.delete(event);
    this.metadata.delete(event);
  }

  clearAll(): void {
    this.handlers.clear();
    this.metadata.clear();
  }

  validateRequired(requiredEvents: string[]): string[] {
    return requiredEvents.filter((event) => !this.hasHandler(event));
  }

  getDiagnostics() {
    const allSignalREvents = getRequiredEvents().map((e) => e.name);
    const registered = this.getAllRegistered();
    const missingCritical = allSignalREvents.filter((e) => !this.hasHandler(e));

    return {
      totalEvents: registered.length,
      registeredEvents: registered,
      handlersPerEvent: Object.fromEntries(
        registered.map((event) => [event, this.handlerCount(event)])
      ),
      criticalMissing: missingCritical,
    };
  }
}
